import { MessageCircle, Camera, Play } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { CompareSlider } from "./CompareSlider";
import { GALLERY_CASES, TIKTOK_VIDEO_ID, TIKTOK_URL, waLink } from "../data/content";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.9 2.9 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .58.05.85.13V9.4a6.33 6.33 0 0 0-.85-.05A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
  </svg>
);

const TikTokCard = () => (
  <div
    data-testid="tiktok-embed-card"
    className="snap-center shrink-0 w-[86vw] sm:w-[560px] lg:w-[620px]"
  >
    <div className="relative aspect-[16/10] overflow-hidden rounded-3xl border border-gold/30 bg-ink">
      {TIKTOK_VIDEO_ID ? (
        <iframe
          title="Vídeo da Gold Service no TikTok"
          src={`https://www.tiktok.com/player/v1/${TIKTOK_VIDEO_ID}?autoplay=0&loop=0`}
          className="absolute inset-0 h-full w-full"
          allow="encrypted-media; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.14),transparent_55%)]">
          <span className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-black border border-white/15 shadow-[0_0_40px_rgba(212,175,55,0.25)]">
            <TikTokIcon className="h-8 w-8 text-zinc-100" />
            <span className="absolute -bottom-1.5 -right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-gold text-ink">
              <Play className="h-3.5 w-3.5 fill-current" />
            </span>
          </span>
          <p className="mt-5 font-display text-xl sm:text-2xl font-bold">
            Serviço em ação, <span className="gold-text">ao vivo</span>
          </p>
          <p className="mt-2 text-sm text-mist max-w-xs">
            Acompanhe os bastidores e resultados dos atendimentos no nosso
            TikTok.
          </p>
          <a
            data-testid="tiktok-card-link"
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/50 px-6 py-3 text-sm font-bold text-gold-light transition-all duration-300 hover:bg-gold hover:text-ink"
          >
            <TikTokIcon className="h-4 w-4" />
            Assistir em @goldservicemg
          </a>
        </div>
      )}
    </div>
    <p className="mt-4 px-1 font-display font-bold text-zinc-50">
      TikTok <span className="text-gold">@goldservicemg</span>
    </p>
    <p className="px-1 text-xs text-mist mt-0.5">Vídeos reais dos nossos atendimentos</p>
  </div>
);

export const Gallery = () => (
  <section
    id="antes-depois"
    className="relative py-16 sm:py-24 lg:py-32 bg-coal/40 overflow-hidden"
  >
    <div className="absolute -bottom-24 -left-24 h-[360px] w-[360px] rounded-full bg-gold/10 blur-[120px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Overline>Galeria de serviços</Overline>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-xl text-balance">
            Antes & <span className="gold-text">depois</span> de quem confia
          </h2>
          <p className="text-sm text-mist max-w-sm">
            Arraste o controle dourado sobre cada foto e compare o resultado
            real dos nossos serviços.
          </p>
        </div>
      </Reveal>
    </div>

    <Reveal delay={0.15}>
      <div
        data-testid="gallery-carousel"
        className="mt-10 flex gap-5 sm:gap-6 overflow-x-auto snap-x snap-mandatory px-4 sm:px-6 lg:px-8 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {GALLERY_CASES.map((c, i) => (
          <figure
            key={c.id}
            data-testid={`gallery-case-${c.id}`}
            className="snap-center shrink-0 w-[86vw] sm:w-[560px] lg:w-[620px]"
          >
            <CompareSlider
              imageAntes={c.imageAntes}
              imageDepois={c.imageDepois}
              testid={i === 0 ? "before-after-slider" : `before-after-slider-${c.id}`}
            />
            <figcaption className="mt-4 flex items-center justify-between gap-4 px-1">
              <div>
                <p className="font-display font-bold text-zinc-50">{c.title}</p>
                <p className="text-xs text-mist mt-0.5">{c.subtitle}</p>
              </div>
              <span className="shrink-0 text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">
                Arraste
              </span>
            </figcaption>
          </figure>
        ))}

        <TikTokCard />

        <div className="snap-center shrink-0 w-[86vw] sm:w-[560px] lg:w-[620px] gold-border-gradient rounded-3xl bg-card flex flex-col items-center justify-center text-center p-8 sm:p-12 min-h-[280px]">
          <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-gold/10 text-gold">
            <Camera className="h-6 w-6" />
          </span>
          <p className="mt-5 font-display text-xl sm:text-2xl font-bold text-balance">
            Seu ambiente pode ser o próximo <span className="gold-text">antes & depois</span>
          </p>
          <p className="mt-2 text-sm text-mist max-w-xs">
            Cada serviço Gold vem com relatório fotográfico do resultado.
          </p>
          <a
            data-testid="gallery-cta-whatsapp"
            href={waLink("Olá, Gold Service! Vi a galeria de antes e depois e quero um orçamento.")}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] hover:-translate-y-0.5"
          >
            <MessageCircle className="h-4 w-4" />
            Pedir orçamento
          </a>
        </div>
      </div>
    </Reveal>
  </section>
);
